import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Building2, Plus, Save, X, Power, ArrowLeft, Link2, ShieldCheck, Phone, MessageSquare, Video, Copy, Eye } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

export default function AdminOrganizations() {
  const [orgs, setOrgs] = useState([]);
  const [uncategorized, setUncategorized] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState({ mode: "list" }); // {mode:'list'} | {mode:'create'} | {mode:'edit', id}

  const load = async () => {
    setLoading(true);
    try {
      const [oRes, uRes] = await Promise.all([
        api.get("/admin/organizations"),
        api.get("/admin/organizations/uncategorized"),
      ]);
      setOrgs(oRes.data || []);
      setUncategorized(uRes.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (view.mode === "create") return <OrgEditor onDone={() => { setView({ mode: "list" }); load(); }} uncategorized={uncategorized} />;
  if (view.mode === "edit") return <OrgEditor orgId={view.id} onDone={() => { setView({ mode: "list" }); load(); }} uncategorized={uncategorized} />;

  return (
    <div data-testid="admin-organizations">
      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900">Organizations</h2>
          <p className="text-sm text-slate-500">Structured client-org identity. Admin-curated. Foundation for employer-facing reports.</p>
        </div>
        <button data-testid="org-new-btn" onClick={() => setView({ mode: "create" })} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider"><Plus className="w-4 h-4" strokeWidth={2.2} /> New Organization</button>
      </div>

      <div className="mb-4 flex items-start gap-2 text-xs text-[#3B597B]"><ShieldCheck className="w-3.5 h-3.5 mt-0.5" strokeWidth={2.2} /><p>Aliases are matched case-insensitively but never fuzzy-matched. A structured Organizations directory replaces free-text company-name matching for future employer reporting.</p></div>

      {/* Uncategorized panel */}
      <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="org-uncategorized-panel">
        <p className="text-xs uppercase tracking-[0.16em] font-semibold text-amber-800 mb-2">Uncategorized company names <span className="text-amber-700/70 normal-case tracking-normal text-[11px]">({uncategorized.length})</span></p>
        {uncategorized.length === 0 ? (
          <p className="text-sm text-slate-500">All submitted company names are associated with an organization.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm">
            {uncategorized.slice(0, 12).map((u) => (
              <div key={u.normalized} className="rounded border border-amber-100 bg-white px-3 py-2 flex items-center justify-between" data-testid={`org-uncat-${u.normalized.replace(/\s+/g, "-")}`}>
                <span className="text-slate-800 truncate mr-2">{u.display_name}</span>
                <span className="text-[11px] text-slate-500">{u.requests} req · {u.unique_employees} emp</span>
              </div>
            ))}
            {uncategorized.length > 12 && <p className="text-[11px] text-slate-500">…and {uncategorized.length - 12} more. Associate them via an organization editor.</p>}
          </div>
        )}
      </div>

      {/* Organizations list */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
        ) : orgs.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="org-empty">No organizations yet. Click <strong>New Organization</strong> to create one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="text-left px-3 py-3">Name</th>
                  <th className="text-left px-3 py-3">Slug</th>
                  <th className="text-left px-3 py-3">Aliases</th>
                  <th className="text-right px-3 py-3">Requests</th>
                  <th className="text-right px-3 py-3">Employees</th>
                  <th className="text-right px-3 py-3">Activities</th>
                  <th className="text-left px-3 py-3">Status</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orgs.map((o) => (
                  <tr key={o.id} data-testid={`org-row-${o.id}`} className="hover:bg-slate-50/60">
                    <td className="px-3 py-3 text-slate-900 font-medium">{o.name}</td>
                    <td className="px-3 py-3 text-slate-500 font-mono text-xs">{o.slug}{o.slug_locked && <span className="ml-1 text-[10px] text-slate-400">(locked)</span>}</td>
                    <td className="px-3 py-3"><div className="flex flex-wrap gap-1">{o.aliases.slice(0, 4).map((a) => <span key={a} className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px]">{a}</span>)}{o.aliases.length > 4 && <span className="text-[11px] text-slate-500">+{o.aliases.length - 4}</span>}</div></td>
                    <td className="px-3 py-3 text-right text-slate-700">{o.request_count}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{o.unique_employees}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{o.activity_count}</td>
                    <td className="px-3 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${o.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{o.status}</span></td>
                    <td className="px-3 py-3 text-right"><button data-testid={`org-edit-${o.id}`} onClick={() => setView({ mode: "edit", id: o.id })} className="text-xs text-[#4A6FA5] hover:text-[#3B597B] font-semibold uppercase tracking-wider">Manage</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function OrgEditor({ orgId, onDone, uncategorized }) {
  const isNew = !orgId;
  const [org, setOrg] = useState(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [aliases, setAliases] = useState([]);
  const [aliasInput, setAliasInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [associating, setAssociating] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/organizations/${orgId}`).then((r) => {
      setOrg(r.data);
      setName(r.data.name);
      setSlug(r.data.slug);
      setAliases(r.data.aliases || []);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [orgId, isNew]);

  const addAlias = () => {
    const v = aliasInput.trim().toLowerCase();
    if (!v) return;
    if (aliases.includes(v)) { setAliasInput(""); return; }
    setAliases([...aliases, v]);
    setAliasInput("");
  };
  const removeAlias = (a) => setAliases(aliases.filter((x) => x !== a));

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required."); return; }
    setSaving(true);
    try {
      if (isNew) {
        const res = await api.post("/admin/organizations", { name: name.trim(), slug: slug.trim() || undefined, aliases });
        toast.success("Organization created");
        // If admin picked uncategorized names, run associate-legacy for the new org
        if (picked.size > 0) {
          await api.post(`/admin/organizations/${res.data.id}/associate-legacy`, { company_names: Array.from(picked) });
          toast.success(`Associated ${picked.size} legacy company names`);
        }
      } else {
        const payload = { name: name.trim(), aliases };
        if (!org.slug_locked && slug.trim() && slug.trim() !== org.slug) payload.slug = slug.trim();
        await api.patch(`/admin/organizations/${orgId}`, payload);
        toast.success("Organization saved");
      }
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const associateLegacy = async () => {
    if (picked.size === 0) { toast.error("Pick at least one company name."); return; }
    setAssociating(true);
    try {
      const res = await api.post(`/admin/organizations/${orgId}/associate-legacy`, { company_names: Array.from(picked) });
      toast.success(`Associated ${res.data.updated_count} request(s) across ${Object.keys(res.data.matched_by).length} names`);
      setPicked(new Set());
      // Reload the org detail for updated aliases/counts
      const r = await api.get(`/admin/organizations/${orgId}`);
      setOrg(r.data); setAliases(r.data.aliases || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setAssociating(false); }
  };

  const toggleStatus = async () => {
    const next = org.status === "active" ? "inactive" : "active";
    try {
      const r = await api.patch(`/admin/organizations/${orgId}`, { status: next });
      setOrg(r.data);
      toast.success(`Marked ${next}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div data-testid="admin-organization-editor">
      <button onClick={onDone} data-testid="org-editor-back" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="w-3.5 h-3.5" /> Back to organizations</button>
      <h2 className="font-heading text-2xl tracking-tight text-slate-900 mb-1">{isNew ? "New organization" : org?.name || "Loading…"}</h2>
      {!isNew && org && (
        <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500 mb-4">
          <span>slug: <code className="text-slate-700">{org.slug}</code>{org.slug_locked && <span className="ml-1 text-slate-400">(locked)</span>}</span>
          <span>·</span>
          <span>{org.request_count} requests · {org.unique_employees} employees · {org.activity_count} activities</span>
          <button data-testid="org-toggle-status" onClick={toggleStatus} className={`ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold uppercase tracking-wider ${org.status === "active" ? "bg-slate-800 text-white hover:bg-slate-900" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}><Power className="w-3.5 h-3.5" strokeWidth={2.2} /> {org.status === "active" ? "Deactivate" : "Reactivate"}</button>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4 mb-6">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-600">Name</span>
          <input data-testid="org-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]" />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-600">Slug</span>
          <input data-testid="org-slug-input" value={slug} onChange={(e) => setSlug(e.target.value)} disabled={!isNew && org?.slug_locked} placeholder={isNew ? "(auto from name if left blank)" : ""} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] disabled:bg-slate-50 disabled:text-slate-400" />
          {!isNew && org?.slug_locked && <span className="mt-1 block text-[11px] text-slate-500">Slug is locked because requests are already associated with this organization.</span>}
        </label>
        <div>
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-600">Aliases</span>
          <p className="mt-0.5 text-[11px] text-slate-500 mb-2">Exact case-insensitive matches only. Normalized to trim + lowercase. Must be unique across all organizations.</p>
          <div className="flex flex-wrap gap-1 mb-2" data-testid="org-alias-chips">
            {aliases.map((a) => (
              <span key={a} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs">
                {a}
                <button onClick={() => removeAlias(a)} className="text-slate-400 hover:text-slate-700" aria-label={`Remove alias ${a}`} data-testid={`org-alias-remove-${a.replace(/\s+/g, "-")}`}><X className="w-3 h-3" /></button>
              </span>
            ))}
            {aliases.length === 0 && <span className="text-xs text-slate-400">No aliases yet.</span>}
          </div>
          <div className="flex gap-2">
            <input data-testid="org-alias-input" value={aliasInput} onChange={(e) => setAliasInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAlias(); } }} placeholder="e.g. acme, acme coffee" className="flex-1 px-3 py-1.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]" />
            <button data-testid="org-alias-add" onClick={addAlias} type="button" className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs font-semibold uppercase tracking-wider hover:bg-slate-900">Add</button>
          </div>
        </div>
        <div className="pt-2 flex justify-end">
          <button data-testid="org-save" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] disabled:opacity-60 text-white text-sm font-semibold uppercase tracking-wider"><Save className="w-4 h-4" strokeWidth={2.2} /> {saving ? "Saving…" : isNew ? "Create organization" : "Save changes"}</button>
        </div>
      </div>

      {!isNew && orgId && <CallAChapSettingsPanel orgId={orgId} orgSlug={org?.slug} />}

      {/* Associate legacy panel */}
      <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-5" data-testid="org-associate-panel">
        <p className="text-xs uppercase tracking-[0.16em] font-semibold text-amber-800 mb-1 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" strokeWidth={2.2} /> Associate legacy company names</p>
        <p className="text-[11px] text-slate-600 mb-3">Select the free-text company names below that belong to this organization. Selected names are added as aliases and matching historical requests are linked to this org. Never fuzzy-matched.</p>
        {uncategorized.length === 0 ? (
          <p className="text-sm text-slate-500">Nothing uncategorized to associate.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-3" data-testid="org-associate-grid">
              {uncategorized.map((u) => {
                const checked = picked.has(u.normalized);
                return (
                  <label key={u.normalized} className={`inline-flex items-start gap-2 rounded border px-3 py-2 text-sm ${checked ? "border-[#4A6FA5] bg-white" : "border-slate-200 bg-white"}`}>
                    <input type="checkbox" checked={checked} data-testid={`org-associate-pick-${u.normalized.replace(/\s+/g, "-")}`} onChange={(e) => {
                      const next = new Set(picked);
                      if (e.target.checked) next.add(u.normalized); else next.delete(u.normalized);
                      setPicked(next);
                    }} className="mt-0.5 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]" />
                    <div className="min-w-0">
                      <p className="text-slate-800 truncate">{u.display_name}</p>
                      <p className="text-[11px] text-slate-500">{u.requests} req · {u.unique_employees} emp · {u.activities} act</p>
                    </div>
                  </label>
                );
              })}
            </div>
            {!isNew && (
              <div className="flex justify-end">
                <button data-testid="org-associate-run" onClick={associateLegacy} disabled={associating || picked.size === 0} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-700 hover:bg-amber-800 disabled:opacity-50 text-white text-sm font-semibold uppercase tracking-wider"><Link2 className="w-4 h-4" strokeWidth={2.2} /> {associating ? "Associating…" : `Associate ${picked.size} name(s)`}</button>
              </div>
            )}
            {isNew && picked.size > 0 && (
              <p className="text-[11px] text-slate-500">These {picked.size} name(s) will be added and associated when you click <strong>Create organization</strong>.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CallAChapSettingsPanel({ orgId, orgSlug }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const employeeUrl = orgSlug ? `${window.location.origin}/care-portal/callachap/${orgSlug}` : "";

  useEffect(() => {
    api.get(`/admin/organizations/${orgId}/callachap-settings`).then((r) => setS(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [orgId]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/organizations/${orgId}/callachap-settings`, s);
      setS(r.data);
      toast.success("CALLACHAP routing saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(employeeUrl); toast.success("Employee URL copied to clipboard"); } catch { toast.error("Could not copy — please copy manually."); }
  };

  if (!s) return <div className="rounded-xl border border-slate-200 bg-white p-5 mb-4 text-sm text-slate-500" data-testid="callachap-settings-loading">Loading CALLACHAP routing…</div>;
  const setF = (patch) => setS({ ...s, ...patch });
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 mb-4" data-testid="callachap-settings-panel">
      <div className="flex items-center gap-2 mb-1"><Phone className="w-4 h-4 text-[#4A6FA5]" strokeWidth={2} /><h3 className="font-heading text-lg text-slate-900">CALLACHAP routing</h3></div>
      <p className="text-[11px] text-slate-500 mb-4">Employee-facing URL: <code className="text-slate-700">{employeeUrl}</code></p>
      <div className="flex flex-wrap gap-2 mb-5">
        <button type="button" onClick={copyUrl} data-testid="callachap-copy-url" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-slate-300 text-xs hover:border-[#4A6FA5]"><Copy className="w-3.5 h-3.5" /> Copy employee URL</button>
        <a href={employeeUrl} target="_blank" rel="noopener noreferrer" data-testid="callachap-preview-url" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-slate-300 text-xs hover:border-[#4A6FA5]"><Eye className="w-3.5 h-3.5" /> Preview employee view</a>
      </div>

      <ChannelBlock icon={Phone} title="Call" testPrefix="cc-call">
        <Toggle checked={s.call_enabled} onChange={(v) => setF({ call_enabled: v })} testId="cc-call-toggle" />
        <Field label="Call number (E.164)" placeholder="+15551234567" value={s.call_number} onChange={(v) => setF({ call_number: v })} testId="cc-call-number" />
        <Field label="Display label (optional)" placeholder="e.g. HR-approved chaplain hotline" value={s.call_display_label} onChange={(v) => setF({ call_display_label: v })} testId="cc-call-label" />
        <p className="col-span-full text-[11px] text-slate-500 leading-relaxed">
          Do not type "24/7" into this org-specific label. CCCHAPS chaplain support is 24/7 on-call site-wide; this label describes only how the organization&apos;s <em>phone routing</em> is presented to employees.
        </p>
      </ChannelBlock>

      <ChannelBlock icon={MessageSquare} title="Text / SMS" testPrefix="cc-text">
        <Toggle checked={s.text_enabled} onChange={(v) => setF({ text_enabled: v })} testId="cc-text-toggle" />
        <Field label="Text number (E.164)" placeholder="+15551234567" value={s.text_number} onChange={(v) => setF({ text_number: v })} testId="cc-text-number" />
        <Field label="Prefill message (optional)" placeholder="Hi CCCHAPS — I'd like to talk." value={s.text_prefill} onChange={(v) => setF({ text_prefill: v })} testId="cc-text-prefill" />
      </ChannelBlock>

      <ChannelBlock icon={Video} title="Video" testPrefix="cc-video">
        <Toggle checked={s.video_enabled} onChange={(v) => setF({ video_enabled: v })} testId="cc-video-toggle" />
        <Field label="Video / scheduling link" placeholder="https://calendly.com/…" value={s.video_link} onChange={(v) => setF({ video_link: v })} testId="cc-video-link" />
        <label className="flex items-start gap-2 text-xs text-slate-700 col-span-full">
          <input type="checkbox" checked={!!s.video_capture_request} onChange={(e) => setF({ video_capture_request: e.target.checked })} data-testid="cc-video-capture" className="mt-0.5 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]" />
          <span>Capture a minimal Care Portal request before redirecting the employee to the scheduler (recommended).</span>
        </label>
      </ChannelBlock>

      <div className="grid md:grid-cols-2 gap-3 pt-4 border-t border-slate-100">
        <Field label="Default chaplain display name (optional)" placeholder="Rev. Jane Chaplain" value={s.default_chaplain_display_name} onChange={(v) => setF({ default_chaplain_display_name: v })} testId="cc-chaplain-name" />
        <Field label="Local phone/text routing hours (optional)" placeholder="e.g. Mon–Fri 8a–5p PT" value={s.hours_note} onChange={(v) => setF({ hours_note: v })} testId="cc-hours" />
        <p className="col-span-full text-[11px] text-slate-500 leading-relaxed">
          Enter <span className="font-semibold">local phone/text routing hours only</span>. CCCHAPS-wide 24/7 on-call chaplain access is stated separately by the Care Portal and does not need to be repeated here.
        </p>
      </div>

      <div className="pt-4 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="cc-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] disabled:opacity-50 text-white text-sm font-semibold uppercase tracking-wider"><Save className="w-4 h-4" strokeWidth={2.2} /> {saving ? "Saving…" : "Save routing"}</button>
      </div>
    </div>
  );
}

function ChannelBlock({ icon: Icon, title, testPrefix, children }) {
  return (
    <div className="mb-4 rounded-lg border border-slate-100 p-3" data-testid={`${testPrefix}-block`}>
      <div className="flex items-center gap-1.5 mb-2 text-slate-800"><Icon className="w-4 h-4 text-[#4A6FA5]" strokeWidth={2} /><h4 className="text-sm font-semibold">{title}</h4></div>
      <div className="grid md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs text-slate-700 col-span-full">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} className="rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]" />
      <span>Enable this channel</span>
    </label>
  );
}

function Field({ label, placeholder, value, onChange, testId }) {
  return (
    <label className="block text-xs">
      <span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]" />
    </label>
  );
}

